import { useState } from 'react'
import { LinkIcon, ScissorsIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * 빠른 추가.
 *
 * 링크를 관리하러 들어온 자리에서 바로 만들 수 있게 한다. 예전에는 모달을
 * 열어야 했다.
 *
 * 현재 보고 있는 폴더에 넣는다. 폴더를 열어 둔 채 만들면 그 폴더에 들어가는
 * 것이 자연스럽고, 아니면 만든 뒤 바로 옮겨야 한다.
 */

interface QuickAddBarProps {
  onSubmit: (originalUrl: string) => Promise<void>
  isSubmitting: boolean
}

/** 사람이 흔히 빠뜨리는 것만 막는다. 자세한 검사는 서버가 한다 */
function looksLikeUrl(value: string): boolean {
  return /^https?:\/\/\S+\.\S+/i.test(value.trim())
}

export default function QuickAddBar({
  onSubmit,
  isSubmitting,
}: QuickAddBarProps) {
  const { t } = useTranslation()
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    const value = url.trim()
    if (!value || isSubmitting) {
      return
    }
    if (!looksLikeUrl(value)) {
      setError(t('bookmark.quickAddInvalid'))
      return
    }
    setError(null)
    await onSubmit(value)
    setUrl('')
  }

  return (
    <div className="flex flex-col gap-1.5">
      <form
        onSubmit={event => {
          event.preventDefault()
          void submit()
        }}
        className="flex items-center gap-2.5 rounded-2xl border border-gray-200 bg-white py-1.5 pl-3.5 pr-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
      >
        <LinkIcon className="h-[18px] w-[18px] shrink-0 text-primary-600 dark:text-primary-400" />
        <input
          type="text"
          inputMode="url"
          value={url}
          onChange={event => {
            setUrl(event.target.value)
            if (error) {
              setError(null)
            }
          }}
          placeholder={t('bookmark.quickAddPlaceholder')}
          aria-label={t('bookmark.quickAddPlaceholder')}
          aria-invalid={error ? true : undefined}
          className="h-10 min-w-0 flex-1 border-none bg-transparent text-[15px] text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
        />
        <button
          type="submit"
          disabled={!url.trim() || isSubmitting}
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[10px] bg-primary-600 px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-200 dark:disabled:bg-gray-600"
        >
          {isSubmitting ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <ScissorsIcon className="h-4 w-4" />
          )}
          {t('bookmark.quickAddSubmit')}
        </button>
      </form>

      {error && (
        <p
          role="alert"
          className="pl-1 text-sm text-rose-600 dark:text-rose-400"
        >
          {error}
        </p>
      )}
    </div>
  )
}
